// src/components/site-editor/panels/PoliciesPanel.js
// Presentation-only Site Editor panel. Per the IA restructure (Phase 2, Decision 2),
// policy editing lives in Settings → Policies. This panel only shows status for the
// three policies and links out to the canonical home — it does NOT allow edits here.
// See docs/DF-IA-Restructure-Spec-May2026.md.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Circle } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { auth } from '../../../config/firebase-config';
import { getPolicies } from '../../../services/policies-service';

const PoliciesPanel = () => {
  const navigate = useNavigate();
  const { state } = useWebsiteBuilder();
  const [canonicalPolicies, setCanonicalPolicies] = useState(null);

  // Read-with-fallback during the Phase 2 migration window: prefer canonical
  // practitioner.policies.{cancellation|privacy|terms}, fall back to the legacy
  // location at draft.contactFooter.{cancellationPolicy|privacyPolicy|termsOfService}.
  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    getPolicies(uid).then(setCanonicalPolicies).catch(() => {});
  }, []);

  const legacy = state.draft?.contactFooter || {};
  const hasCancellation = !!(canonicalPolicies?.cancellation || legacy.cancellationPolicy);
  const hasPrivacy = !!(canonicalPolicies?.privacy?.content || legacy.privacyPolicy?.content);
  const hasTerms = !!(canonicalPolicies?.terms?.content || legacy.termsOfService?.content);

  const policyRows = [
    { label: 'Cancellation Policy', isCreated: hasCancellation },
    { label: 'Privacy Policy', isCreated: hasPrivacy },
    { label: 'Terms of Service', isCreated: hasTerms },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Policies</h3>
        <p className="text-sm text-gray-600">
          Cancellation, privacy, and terms of service are now managed in Settings.
          This panel only shows status — open Settings → Policies to edit.
        </p>
      </div>

      {/* Status list */}
      <div
        style={{
          padding: '12px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {policyRows.map(({ label, isCreated }) => (
            <div
              key={label}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}
            >
              {isCreated ? (
                <CheckCircle size={16} color="#059669" />
              ) : (
                <Circle size={16} color="#D1D5DB" />
              )}
              <span style={{ color: isCreated ? '#111827' : '#9CA3AF' }}>
                {label}
                {isCreated ? (
                  <span style={{ color: '#059669', marginLeft: 6 }}>— configured</span>
                ) : (
                  <span style={{ marginLeft: 6 }}>— not yet created</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Action — link out to the canonical home in Settings */}
      <button
        onClick={() => navigate('/dashboard/settings/policies')}
        className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
      >
        Manage Policies in Settings <ArrowRight size={14} />
      </button>

      {/* Footer note */}
      <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-600 leading-relaxed">
          <strong className="font-semibold text-gray-900">Footer integration:</strong>{' '}
          Policy links continue to appear automatically in your website footer once each
          policy is created. No setup needed here.
        </p>
      </div>
    </div>
  );
};

export default PoliciesPanel;
